import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BFF_BASE_URL } from '@apis/auth';
import type { AiGuideImage as AiGuideImageData } from '@apis/aiAgent';
import { ScreenshotLightbox } from '@components/ui/ScreenshotLightbox/ScreenshotLightbox';
import { ExportSelectable, type IExportSelectionProps } from './ExportSelectable';
import styles from './AiGuideImage.module.css';

interface IAiGuideImageProps extends IExportSelectionProps {
  image: AiGuideImageData;
}

/**
 * A Help Center screenshot shown beside a step of an answer, captioned with
 * the guide and section it comes from. A click opens it full size, the way the
 * guide reader does. The src is the backend's asset path, pointed at the API
 * origin as the Help Center points it (`rebaseHelpAssets`): the session cookie
 * is SameSite=Lax, so a plain `<img>` stays authenticated.
 */
export function AiGuideImage(props: IAiGuideImageProps) {
  const { image, ...selection } = props;
  const { t } = useTranslation();
  const [zoomed, setZoomed] = useState(false);
  const src = `${BFF_BASE_URL}${image.src}`;
  return (
    <ExportSelectable {...selection}>
      <figure className={styles.figure}>
        <button type="button" className={styles.zoom} onClick={() => setZoomed(true)} aria-label={t('aiAgent.screenshot.zoom', { caption: image.caption })}>
          {/* The size the guide stored, so the answer does not jump as the image loads. */}
          <img src={src} alt="" loading="lazy" width={image.width} height={image.height} className={styles.image} />
        </button>
        <figcaption className={styles.caption} title={image.caption}>{image.caption}</figcaption>
      </figure>
      {zoomed ? <ScreenshotLightbox src={src} onClose={() => setZoomed(false)} /> : null}
    </ExportSelectable>
  );
}
